import clsx from 'clsx';
import React, { useRef, useImperativeHandle, useCallback } from 'react';
import PropTypes from 'prop-types';
import RootCloseWrapper from '../overlays/RootCloseWrapper.js';
import { getClassSet, prefix, splitBsPropsAndOmit } from './utils/bootstrapUtils';
import createChainedFunction from './utils/createChainedFunction';
import ValidComponentChildren from './utils/ValidComponentChildren';
import { jsx as _jsx } from "react/jsx-runtime";
const DropdownMenu = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    open,
    pullRight = false,
    labelledBy,
    onSelect,
    className,
    rootCloseEvent,
    children,
    onClose,
    bsClass = 'dropdown-menu',
    ...props
  } = _ref;
  const menuRef = useRef(null);
  const getFocusableMenuItems = useCallback(() => {
    const node = menuRef.current;
    if (!node) {
      return [];
    }
    return Array.from(node.querySelectorAll('[tabIndex="-1"]'));
  }, []);
  const getItemsAndActiveIndex = useCallback(() => {
    const items = getFocusableMenuItems();
    const activeIndex = items.indexOf(document.activeElement);
    return {
      items,
      activeIndex
    };
  }, [getFocusableMenuItems]);
  const focusNext = useCallback(() => {
    const {
      items,
      activeIndex
    } = getItemsAndActiveIndex();
    if (items.length === 0) {
      return;
    }
    const nextIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
    items[nextIndex].focus();
  }, [getItemsAndActiveIndex]);
  const focusPrevious = useCallback(() => {
    const {
      items,
      activeIndex
    } = getItemsAndActiveIndex();
    if (items.length === 0) {
      return;
    }
    const prevIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
    items[prevIndex].focus();
  }, [getItemsAndActiveIndex]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    focusNext,
    focusPrevious
  }), [focusNext, focusPrevious]);
  const handleKeyDown = useCallback(event => {
    switch (event.keyCode) {
      case 40:
        // keycode.codes.down
        focusNext();
        event.preventDefault();
        break;
      case 38:
        // keycode.codes.up
        focusPrevious();
        event.preventDefault();
        break;
      case 27: // keycode.codes.esc
      case 9:
        // keycode.codes.tab
        onClose(event, {
          source: 'keydown'
        });
        break;
      default:
    }
  }, [focusNext, focusPrevious, onClose]);
  const handleRootClose = useCallback(event => {
    onClose(event, {
      source: 'rootClose'
    });
  }, [onClose]);
  const [bsProps, elementProps] = splitBsPropsAndOmit(props, ['onClose']);
  const classes = {
    ...getClassSet({
      ...bsProps,
      bsClass
    }),
    [prefix({
      ...bsProps,
      bsClass
    }, 'right')]: pullRight
  };
  return /*#__PURE__*/_jsx(RootCloseWrapper, {
    disabled: !open,
    onRootClose: handleRootClose,
    event: rootCloseEvent,
    children: /*#__PURE__*/_jsx("ul", {
      ...elementProps,
      role: "menu",
      className: clsx(className, classes),
      "aria-labelledby": labelledBy,
      ref: menuRef,
      children: ValidComponentChildren.map(children, child => /*#__PURE__*/React.cloneElement(child, {
        onKeyDown: createChainedFunction(child.props.onKeyDown, handleKeyDown),
        onSelect: createChainedFunction(child.props.onSelect, onSelect)
      }))
    })
  });
});
DropdownMenu.displayName = 'DropdownMenu';
export default DropdownMenu;